import { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";

import Spinner from "./ui/Spinner";
import Button from "./ui/Button";
import { useViewer } from "../services/viewer";
import { provisionMyFamily } from "../services/household";

/**
 * Gives somebody who belongs nowhere a home of their own, without anybody at CTS.
 *
 * **The problem.** A consumer who buys one sensor signs up, and the tenant their
 * token names does not exist, because nothing ever created it. Until now that was
 * three manual acts: a tenant row written by hand in AWS, an Auth0 account created in
 * its dashboard, and an email. This is the screen that removes them (REQ-TEN-3).
 *
 * **Who reaches it.** Only a viewer `/me` reports with no tenant AND no
 * organizations. An organization member is `OrganizationGate`'s business and is sent
 * back through sign-in with their organization instead -- it runs above this one.
 *
 * Neither check is load-bearing on its own, and it is worth being exact about why.
 * `/me` reads the organization list inside a try/catch that logs and continues, and
 * `MeDto.Organizations` initialises to an empty list -- so "belongs to no
 * organization" and "we could not read their organizations" arrive here as the same
 * `[]`. One swallowed Auth0 lookup defeats this check and `OrganizationGate`'s
 * ordering at the same time, because both read the same field. **The server is what
 * actually holds**: `TenantProvisioning.Decide` reads the caller's organizations
 * directly and refuses when it cannot, so a provision cannot happen on a failed
 * lookup even though this gate would have asked for one.
 *
 * A family invitee is likewise refused by the server rather than by this gate. Their
 * account holds `app_metadata.TenantId` from the invitation, but a token minted before
 * that write carries no tenant claim, so they can arrive here looking brand new --
 * which is exactly what `ProvisionFamilyForCaller`'s re-read exists to catch.
 *
 * **Why it ends in a sign-in.** The tenant claim is Auth0-signed and the token in the
 * browser was minted before the household existed, so it carries no tenant and `/me`
 * will keep saying so however many times it is asked. Nothing the API returns can
 * change a token already issued -- the server says `reauthentication_required`, and
 * this honours it rather than reloading and finding the same answer.
 *
 * A full `loginWithRedirect`, not a silent renewal: `onExecutePostLogin` is not known
 * to run on a refresh-token exchange in this tenant, so a silently renewed token may
 * come back with no claim and leave the person in a loop that looks like a bug in
 * their account. The slower path is the one that is known to work.
 */
export default function HouseholdGate({
  children,
}: {
  children: React.ReactNode;
}) {
  const { loginWithRedirect } = useAuth0();
  const { data: viewer, isPending, isError } = useViewer();

  // No tenant and nothing to sign in to. The organizations check is not redundant
  // with OrganizationGate running above: if that gate's redirect does not take, its
  // viewer still has no tenant, and provisioning a personal household for an
  // organization member is exactly the stranding this whole feature exists to avoid.
  const needsHousehold =
    !!viewer &&
    !viewer.tenant_id &&
    (viewer.organizations ?? []).length === 0;

  const [state, setState] = useState<"idle" | "working" | "failed">("idle");
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!needsHousehold || state !== "idle") return;
    if (alreadyTried()) {
      setState("failed");
      return;
    }

    // Recorded BEFORE the request, not after it succeeds. The success path ends in a
    // redirect that replaces this page, so there is no "after" to record in -- and
    // the flag's job is to stop a second attempt, which is needed most precisely when
    // the first one did something unexpected.
    //
    // A record that will not survive stops us here. Without the check, a browser whose
    // setItem throws but whose getItem works -- Safari and iOS private browsing --
    // re-provisions and signs in again on every visit, each turn doing a write and a
    // role reconciliation. Nothing is written in that case, so there is nothing to
    // recover from; the person is told instead.
    if (!remember()) {
      setState("failed");
      return;
    }

    setState("working");

    provisionMyFamily()
      .then(() => {
        // Nothing to render after this; the page is replaced. Catching the redirect
        // separately: if IT fails, the household exists and only the token is stale,
        // so the generic "we couldn't set up your home" would be false.
        // Promise.resolve around it, not a bare .catch: loginWithRedirect is typed as
        // returning a promise, but it is not this component's job to assume every
        // implementation does -- and a .catch on undefined throws synchronously, which
        // would land in the outer catch and tell the person their home could not be
        // set up when it had just been created. A test caught exactly that.
        Promise.resolve(loginWithRedirect()).catch(() => {
          setMessage(
            "Your home is ready, but we couldn't sign you back in. " +
              "Please sign in again."
          );
          setState("failed");
        });
      })
      .catch((error: unknown) => {
        // A 409 means the server already resolves them to a tenant -- the household
        // exists and only this token is stale, so signing in again is the remedy and
        // retrying the write is not. The endpoint documents the status; without this
        // branch the person parks on an error whose only button re-POSTs.
        const status = (error as { response?: { status?: number } })?.response?.status;

        if (status === 409) {
          Promise.resolve(loginWithRedirect()).catch(() => {
            setMessage(
              "You already have a home, but we couldn't sign you back in. " +
                "Please sign in again."
            );
            setState("failed");
          });
          return;
        }

        setMessage(messageFrom(error));
        setState("failed");
      });
  }, [needsHousehold, state, loginWithRedirect]);

  // The viewer gates everything below it, and a failure to read it is handled where
  // it already is. This component only decides about households.
  //
  // Children, never null: this sits above the whole app, so returning nothing here
  // would blank the dashboard for every viewer while /me is in flight -- which is
  // everybody, every load. The suite did not catch that until a review mutated it.
  if (isPending || isError || !viewer) return <>{children}</>;

  if (!needsHousehold) return <>{children}</>;

  if (state === "failed") {
    // onRetry clears the flag and nothing else: Stuck reloads, and the reload is what
    // re-enters this gate with a clean slate. Returning to "idle" in place as well
    // would start a second attempt that the reload then interrupts -- and that
    // attempt re-sets the flag, so the reloaded page would open straight back on this
    // screen without having tried. Found by the test for this button.
    return <Stuck message={message} onRetry={forget} />;
  }

  return <Working />;
}

/**
 * Whether this browser session has already tried.
 *
 * **The loop this prevents is the reason it exists**, and it is the same shape as
 * `OrganizationGate`'s. Provisioning succeeds, the browser signs in again, and the
 * new token still carries no tenant -- because the Action did not pick the metadata
 * up, or the row was written and the role assignment failed. The condition that
 * triggered this is then still true, and without a record that is an endless
 * sign-in cycle in which the person never reaches a screen that could explain it.
 *
 * `sessionStorage`, so signing out and back in tries again. Wrapped, because storage
 * throws in a private window -- and a browser that cannot remember is treated as
 * having already tried, since a lost flag beats a loop.
 */
const KEY = "cts.household-provision-attempted";

function alreadyTried(): boolean {
  try {
    return window.sessionStorage.getItem(KEY) === "1";
  } catch {
    return true;
  }
}

/**
 * Records the attempt, and says whether the record will survive.
 *
 * **It verifies the write rather than assuming it.** The previous version caught and
 * ignored a failing `setItem`, with a comment claiming `alreadyTried` would answer
 * true anyway — which is only so when reads fail *too*. Safari and iOS private
 * browsing are the well-known case where `getItem` works and `setItem` throws, and
 * there every visit re-provisions and signs in again: the loop this flag exists to
 * stop, with each turn also doing a write and a role reconciliation. An adversarial
 * review reproduced it three visits deep.
 *
 * So the caller is told. A record that did not stick is treated exactly like one that
 * says "already tried", because an attempt we cannot remember is an attempt we must
 * not repeat.
 */
function remember(): boolean {
  try {
    window.sessionStorage.setItem(KEY, "1");
    // Read back, not assumed: a storage that accepts the write and discards it is the
    // same hazard as one that throws, and costs nothing to rule out.
    return window.sessionStorage.getItem(KEY) === "1";
  } catch {
    return false;
  }
}

function forget(): void {
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    /* see remember */
  }
}

/**
 * The server's own words when they were written for the person; ours otherwise.
 *
 * **Gated on the status, which it did not used to be.** A 4xx from this endpoint
 * carries a refusal written to be read -- an unconfirmed email address is the common
 * one, and the person can act on it where we cannot. A 5xx carries whatever the
 * failure was, and this API's envelope will happily put an exception's text there, so
 * repeating it means a table name or a null-reference message on a customer's screen.
 * Anything at or above 500 gets our own wording.
 *
 * Nothing here can return an empty string: a blank message renders as a blank screen,
 * which reads as the app being broken rather than as an explanation.
 */
function messageFrom(error: unknown): string | null {
  const response = (error as { response?: { status?: unknown; data?: unknown } })
    ?.response;

  const status = typeof response?.status === "number" ? response.status : 0;
  if (status === 0 || status >= 500) return null;

  const message = (response?.data as { message?: unknown })?.message;

  return typeof message === "string" && message.trim() !== "" ? message : null;
}

function Working() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 text-white">
      <Spinner size={64} />
      <p className="text-ink-3 text-sm">Setting up your home…</p>
    </div>
  );
}

/**
 * Shown when it did not work, saying which of the two things happened.
 *
 * The server's message is used when it sent one -- those are written to be read, and
 * the commonest is an unconfirmed email address, which the person can act on and we
 * cannot. Ours is the fallback, and it does not guess.
 */
function Stuck({
  message,
  onRetry,
}: {
  message: string | null;
  onRetry: () => void;
}) {
  return (
    <div className="min-h-screen flex items-center justify-center p-6 text-white">
      <div className="max-w-md text-center space-y-4">
        <h1 className="text-xl font-semibold">We couldn't set up your home</h1>
        <p className="text-ink-3 text-sm">
          {message ??
            "You're signed in, but we couldn't finish setting up your home, so " +
              "there's nothing here yet."}
        </p>
        <Button
          onClick={() => {
            onRetry();
            window.location.reload();
          }}
        >
          Try again
        </Button>
      </div>
    </div>
  );
}
